import {
  Box,
  ClickAwayListener,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { AnalyticsEventName } from '@schooly/analytics';
import { trackAmplitudeEvent } from '@schooly/analytics';
import { InboxMessage, RootEntityType, useMarkAsReadMutation } from '@schooly/api';
import { useUserNotifications } from '@schooly/context';
import { useFlag } from '@schooly/hooks/use-flag';
import {
  BellIcon,
  Counter,
  Loading,
  ModalContent,
  ModalMain,
  TagSelect,
  TagSelectCounter,
} from '@schooly/style';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { ModalHeaderV2 } from '../../components/uikit-components/Modal/ModalHeaderV2';
import { EmptyInbox } from './EmptyInbox';
import { MessagePreview } from './MessagePreview';
import { NotificationCard } from './NotificationCard';
import {
  MessageNotification,
  sortNotificationsByDate,
  ThreadNotification,
  transformToMessageNotifications,
  transformToThreadNotifications,
} from './utils';

export const NotificationsModal: React.FC = () => {
  const [isNotificationsModalOpen, openNotificationsModal, closeNotificationsModal] = useFlag();
  const [messagePreview, setMessagePreview] = useState<InboxMessage | null>(null);
  const [showUnread, setShowUnread] = useState(false);

  const {
    threadsUnread,
    isThreadsUnreadLoading,
    messages,
    isMessagesLoading,
    invalidateNotifications,
  } = useUserNotifications();
  const navigate = useNavigate();
  const threadsUnreadData = threadsUnread?.unread_threads?.[RootEntityType.MESSAGE];
  const { formatMessage } = useIntl();

  const messagesUnread = useMemo(
    () => messages?.filter((message) => !message.recipient_read).length,
    [messages],
  );

  const { mutateAsync: markAsRead } = useMarkAsReadMutation();
  const unreadThreads = threadsUnreadData ? Object.keys(threadsUnreadData).length : 0;
  const hasUnread = unreadThreads > 0 || !!messagesUnread;
  const totalUnread = unreadThreads + (messagesUnread ?? 0);
  const isEmpty = !unreadThreads && !messages?.length;
  const isLoading = isThreadsUnreadLoading || isMessagesLoading;

  const [threadNotifications, messageNotifications] = useMemo(() => {
    return [
      transformToThreadNotifications(threadsUnreadData ?? {}),
      transformToMessageNotifications(messages ?? []),
    ];
  }, [threadsUnreadData, messages]);

  const filteredNotifications = useMemo(() => {
    const allNotifications = [...threadNotifications, ...messageNotifications];
    if (showUnread) {
      return sortNotificationsByDate(
        allNotifications.filter(
          (n) => n.type === 'thread' || (n.type === 'message' && !n.message.recipient_read),
        ),
      );
    }

    return sortNotificationsByDate(allNotifications);
  }, [showUnread, threadNotifications, messageNotifications]);

  const handleThreadNotificationClick = async (notification: ThreadNotification) => {
    const singleThread =
      notification.unreadThreads.length === 1 ? notification.unreadThreads[0] : null;
    const tab = singleThread ? `threads&id=${singleThread.thread_id}` : 'threads';

    trackAmplitudeEvent({
      name: AnalyticsEventName.ClickNotification,
      payload: { thread_id: singleThread?.thread_id, message_id: notification.rootEntity.id },
    });

    navigate(`/messages/${notification.rootEntity.id}?tab=${tab}`);
    closeNotificationsModal();
  };

  const handleMessageNotificationClick = async (notification: MessageNotification) => {
    if (!notification.message.recipient_read) {
      await markAsRead({
        messageId: notification.message.id,
        recipientId: notification.message.recipient_id,
      });

      invalidateNotifications();
    }
    setMessagePreview(notification.message);
  };

  const handleButtonClick = useCallback(() => {
    if (isNotificationsModalOpen) {
      closeNotificationsModal();
    } else {
      openNotificationsModal();

      trackAmplitudeEvent({
        name: AnalyticsEventName.NotificationsOpenWeb,
      });

      invalidateNotifications();
    }
  }, [
    closeNotificationsModal,
    invalidateNotifications,
    isNotificationsModalOpen,
    openNotificationsModal,
  ]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <Tooltip
      open={isNotificationsModalOpen}
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 40],
            },
          },
        ],
      }}
      placement="right-end"
      componentsProps={{
        tooltip: {
          sx: (theme) => ({
            width: 600,
            maxWidth: 600,
            height: 810,
            padding: 0,
            overflow: 'hidden',
            margin: `${theme.spacing(0.5, 0, 0)} !important`,
          }),
        },
      }}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      title={
        <ClickAwayListener onClickAway={closeNotificationsModal}>
          {messagePreview ? (
            <MessagePreview message={messagePreview} onClose={() => setMessagePreview(null)} />
          ) : (
            <Stack sx={{ height: '100%' }} onWheel={handleWheel}>
              <ModalHeaderV2
                title={
                  <>
                    <Typography variant="h1">
                      {formatMessage({ id: 'school-sections-Notifications' })}
                    </Typography>
                  </>
                }
                active
                subHeader={
                  !isEmpty ? (
                    <Stack flexDirection="row" gap={1} mb={1} mt={2}>
                      <TagSelect
                        label={
                          <Stack direction="row" alignItems="center" gap={0.5}>
                            {formatMessage({ id: 'messages-InboxTitle' })}
                            {messagesUnread ? (
                              <TagSelectCounter inverse>{messagesUnread}</TagSelectCounter>
                            ) : null}
                          </Stack>
                        }
                      />

                      <TagSelect
                        label={
                          <Stack direction="row" alignItems="center" gap={0.5}>
                            {formatMessage({ id: 'messages-Comments' })}
                            {unreadThreads ? (
                              <TagSelectCounter inverse>{unreadThreads}</TagSelectCounter>
                            ) : null}
                          </Stack>
                        }
                      />
                    </Stack>
                  ) : undefined
                }
              >
                {!isEmpty && (
                  <FormControlLabel
                    control={
                      <Switch checked={showUnread} onChange={() => setShowUnread((u) => !u)} />
                    }
                    label={<FormattedMessage id="messages-UnreadOnly" />}
                    sx={{
                      m: 0,
                      color: showUnread ? 'text.primary' : 'text.secondary',
                      whiteSpace: 'nowrap',
                    }}
                  />
                )}
              </ModalHeaderV2>

              <ModalMain sx={{ flex: 1, overflow: 'hidden' }}>
                <ModalContent
                  sx={{
                    paddingY: 1.75,
                    height: '100%',
                  }}
                  active
                >
                  {isLoading ? (
                    <Stack height={600}>
                      <Loading />
                    </Stack>
                  ) : (
                    <Stack flex={1}>
                      {Object.entries(filteredNotifications).map(
                        ([dateCategory, dateNotifications]) =>
                          dateNotifications.length > 0 && (
                            <Box key={dateCategory} mb={2}>
                              <Typography variant="h4" mb={0.25}>
                                {formatMessage({ id: dateCategory })}
                              </Typography>
                              {dateNotifications.map((notification) => (
                                <Box key={notification.key}>
                                  <NotificationCard
                                    notification={notification}
                                    onNotificationClick={() =>
                                      notification.type === 'thread'
                                        ? handleThreadNotificationClick(notification)
                                        : handleMessageNotificationClick(notification)
                                    }
                                  />
                                </Box>
                              ))}
                            </Box>
                          ),
                      )}
                      {!Object.values(filteredNotifications).every((v) => v.length === 0) ? (
                        <Box mt={10}>
                          <EmptyInbox
                            title={
                              <Typography width="100px" textAlign="center" color="common.grey2">
                                {formatMessage({ id: 'messages-AllNotifications' })}
                              </Typography>
                            }
                          />
                        </Box>
                      ) : (
                        <Stack height={isEmpty ? 700 : 600}>
                          <NotificationEmptyState hasUnread={hasUnread} isEmpty={isEmpty} />
                        </Stack>
                      )}
                    </Stack>
                  )}
                </ModalContent>
              </ModalMain>
            </Stack>
          )}
        </ClickAwayListener>
      }
    >
      <IconButton
        onClick={handleButtonClick}
        sx={(theme) => ({
          svg: {
            width: 14,
            height: 18,
            color: isNotificationsModalOpen
              ? theme.palette.background.paper
              : theme.palette.primary.main,
          },
          padding: theme.spacing(0.75, 1),
          border: 'none',
          background: isNotificationsModalOpen
            ? theme.palette.primary.main
            : theme.palette.background.paper,

          ':hover': {
            background: theme.palette.primary.light,
            svg: {
              color: theme.palette.background.paper,
            },
          },
        })}
      >
        {!!totalUnread && (
          <Counter
            sx={{
              position: 'absolute',
              backgroundColor: 'success.main',
              right: -2,
              top: -2,
              width: 16,
              height: 16,
            }}
          >
            <Typography sx={{ verticalAlign: 'top' }} variant="body3">
              {totalUnread}
            </Typography>
          </Counter>
        )}
        <BellIcon />
      </IconButton>
    </Tooltip>
  );
};

type NotificationEmptyStateProps = {
  hasUnread: boolean;
  isEmpty: boolean;
};

const NotificationEmptyState = ({ hasUnread, isEmpty }: NotificationEmptyStateProps) => {
  const { formatMessage } = useIntl();

  const emptyTitle = isEmpty ? formatMessage({ id: 'messages-NoNotifications' }) : null;
  const noUnreadTitle = !hasUnread ? formatMessage({ id: 'messages-NoUnreadNotifications' }) : null;

  const title = emptyTitle || noUnreadTitle;
  if (!title) return null;

  return (
    <EmptyInbox
      size="large"
      title={
        <Typography mt={2} variant="h2" textAlign="center" width={emptyTitle ? '260px' : '100%'}>
          {title}
        </Typography>
      }
    />
  );
};
